import { computed, type ComputedRef } from 'vue'

import { useRoute, useRouter } from 'nuxt/app'

import { useCoursesSearchContext } from '@courses/search-layer/composables/useCoursesSearchContext'

import { useCommunitiesSearchContext } from '@communities/common-layer/composables/useCommunitiesSearchContext'

import { isEventsDirectoryRoute, useEventsSearchContext } from '@clientportal/cp-events-layer/composables/useEventsSearchContext'

import { useDiscoverSearchContext } from '@clientportal/discover-layer/composables/useDiscoverSearchContext'
import { isDiscoverPath } from '@clientportal/discover-layer/utils/discover-listing.utils'

import type { SearchContext } from '@common/shared-ui-layer/types/search-context'

/** Course catalogue, product, search and my-library routes all search the courses context. */
const isCoursesSearchRoute = (name: string, path: string): boolean =>
  path === '/courses' ||
  path.startsWith('/courses/') ||
  name.startsWith('courses-products') ||
  name.startsWith('courses-search') ||
  name === 'my-library' ||
  path.includes('/courses/my-library') ||
  path.includes('/communities/my-library')

export function useActiveSearchContext(): {
  searchContext: ComputedRef<SearchContext | null>
} {
  const route = useRoute()
  const router = useRouter()

  const coursesContext = useCoursesSearchContext(route, router)
  const communitiesContext = useCommunitiesSearchContext(route, router)
  const eventsContext = useEventsSearchContext(route)
  const discoverContext = useDiscoverSearchContext(route, router)

  const searchContext = computed<SearchContext | null>(() => {
    const name = route.name?.toString() ?? ''
    const path = route.path?.toString() ?? ''

    // Checked before the courses branch: Discover owns `/discover` outright and must not fall
    // through to the courses context, which would navigate away to the courses search page.
    if (isDiscoverPath(path)) {
      return discoverContext
    }
    if (isCoursesSearchRoute(name, path)) {
      return coursesContext
    }
    if (name.startsWith('communities-groups-')) {
      return communitiesContext
    }
    if (isEventsDirectoryRoute(route)) {
      return eventsContext
    }
    return null
  })

  return { searchContext }
}
